import Image from 'next/image';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';
import Lightbox, {
  isImageFitCover,
  isImageSlide,
  useLightboxProps,
  useLightboxState,
} from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Counter from 'yet-another-react-lightbox/plugins/counter';

import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import styles from './Styles.module.css';

const LightboxComponent = ({ portal, images, toggler, setToggler }) => {
  const t = useTranslations();

  return (
    <Lightbox
      portal={{
        root: portal
      }}
      className={classNames('z-100', styles.lightbox)}
      open={!!toggler}
      index={Number(toggler)}
      close={() => setToggler(false)}
      slides={(images || []).map((photo) => ({
        alt: photo.altText || '',
        header: photo.caption || t('apartment.gallery.photo'),
        src: photo.url,
        type: 'image',
        imageFit: 'contain',
      }))}
      render={{
        slide: NextJsImage,
        slideContainer: ({ slide, children }) => (
          <div className='relative'>
            {children}
            <div className="yarl__slide_captions_container yarl__slide_title_container">
              <div className="yarl__slide_title">{slide.header}</div>
            </div>
          </div>
        ),
        iconPrev: () => (
          <span className="btn btn-icon max-sm:btn-sm max-xl:bg-primary-50/70">
            <i className="icon-expand_left text-[24px] icon-event max-xl:text-primary-700" />
          </span>
        ),
        iconNext: () => (
          <span className="btn btn-icon max-sm:btn-sm max-xl:bg-primary-50/70">
            <i className="icon-expand_right text-[24px] icon-event max-xl:text-primary-700" />
          </span>
        ),
        iconClose: () => (
          <span className="btn btn-icon max-sm:btn-sm">
            <i className="icon-close_round text-[24px] icon-event" />
          </span>
        ),
      }}
      counter={{ container: { style: { bottom: 'unset', top: 0 } } }}
      controller={{
        touchAction: 'pan-y',
        closeOnPullUp: true,
      }}
      noScroll={{
        disabled: true,
      }}
      plugins={[Counter, Captions]}
    />
  );
};

function NextJsImage({ slide, offset, rect }) {
  const {
    on: { click },
    carousel: { imageFit },
  } = useLightboxProps();

  const { currentIndex } = useLightboxState();

  const cover = isImageSlide(slide) && isImageFitCover(slide, imageFit);

  if (!isNextJsImage(slide)) return undefined;

  const width = !cover
    ? Math.round(
        Math.min(rect.width, (rect.height / slide.height) * slide.width),
      )
    : rect.width;

  const height = !cover
    ? Math.round(
        Math.min(rect.height, (rect.width / slide.width) * slide.height),
      )
    : rect.height;

  return (
    <div style={{ position: 'relative', width, height }}>
      <Image
        fill
        alt=""
        src={slide}
        loading="eager"
        draggable={false}
        placeholder={slide.blurDataURL ? 'blur' : undefined}
        style={{
          objectFit: cover ? 'cover' : 'contain',
          cursor: click ? 'pointer' : undefined,
        }}
        sizes={`${Math.ceil((width / window.innerWidth) * 100)}vw`}
        onClick={
          offset === 0 ? () => click?.({ index: currentIndex }) : undefined
        }
      />
    </div>
  );
}

function isNextJsImage(slide) {
  return (
    isImageSlide(slide) &&
    typeof slide.width === 'number' &&
    typeof slide.height === 'number'
  );
}

export default LightboxComponent;
